import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken } from '@/redux/modules/account/user/user.selectors';
import { getDeployment } from '@/redux/modules/config';

import type { SimilarItemAlert, SimilarItemAlertsSort } from './similarItemAlerts.types';

import {
    deleteSimilarItemAlert,
    getSimilarItemAlert,
    getSimilarItemAlerts,
    patchAllSimilarItemAlertsEmail,
    patchSimilarItemAlertEmail,
    postSimilarItemAlert,
} from './similarItemAlerts.api';
import { isSimilarItemAlertSaving, shouldFetchSimilarItemAlert } from './similarItemAlerts.selectors';

export const fetchSimilarItemAlert = createAsyncThunk<number, { lotId: number }>(
    'la/ui/similarItemAlerts/fetchSimilarItemAlert',
    async ({ lotId }, { getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        const { payload } = await getSimilarItemAlert({ authToken, deployment, lotId });
        return payload.savedItemId;
    },
    {
        condition: ({ lotId }, { getState }) => shouldFetchSimilarItemAlert(getState(), lotId),
    }
);

export const submitSimilarItemAlert = createAsyncThunk<number, { lotId: number; lotTitle: string }>(
    'la/ui/similarItemAlerts/submitSimilarItemAlert',
    async ({ lotId, lotTitle }, { getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        const { payload } = await postSimilarItemAlert({ authToken, deployment, lotId, lotTitle });
        return payload.savedItemId;
    },
    {
        // Guards a double-click only. Deliberately not gated on the lookup's flag: the two race on
        // the post-login path and a shared guard would drop the user's action.
        condition: ({ lotId }, { getState }) => !isSimilarItemAlertSaving(getState(), lotId),
    }
);

type FetchSimilarItemAlertsResult = {
    items: SimilarItemAlert[];
    page: number;
    pageSize: number;
    totalRecords: number;
};

export const fetchSimilarItemAlerts = createAsyncThunk<
    FetchSimilarItemAlertsResult,
    { keyword?: string; page: number; pageSize: number; sort: SimilarItemAlertsSort }
>('la/ui/similarItemAlerts/fetchSimilarItemAlerts', async ({ keyword, page, pageSize, sort }, { getState }) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const deployment = getDeployment(state);

    const { payload } = await getSimilarItemAlerts({ authToken, deployment, keyword, page, pageSize, sort });
    return {
        items: payload.data ?? [],
        page: payload.page,
        pageSize: payload.pageSize,
        totalRecords: payload.totalRecords,
    };
});

export const toggleSimilarItemAlertEmail = createAsyncThunk<void, { savedItemId: number; sendAlert: boolean }>(
    'la/ui/similarItemAlerts/toggleSimilarItemAlertEmail',
    async ({ savedItemId, sendAlert }, { getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        await patchSimilarItemAlertEmail({ authToken, deployment, savedItemId, sendAlert });
    }
);

export const toggleAllSimilarItemAlertsEmail = createAsyncThunk<void, { sendAlert: boolean }>(
    'la/ui/similarItemAlerts/toggleAllSimilarItemAlertsEmail',
    async ({ sendAlert }, { getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        await patchAllSimilarItemAlertsEmail({ authToken, deployment, sendAlert });
    }
);

// Delete from a management list. Unlike `removeSimilarItemAlert` (the PDP/Price-Results toggle,
// keyed by lotId for its saving guard), this drops the row from `list` and keeps `lotId` so the
// reducer can also clear the per-lot lookup cache — the PDP for that lot must not still read ON.
export const deleteSimilarItemAlertFromList = createAsyncThunk<void, { lotId: number; savedItemId: number }>(
    'la/ui/similarItemAlerts/deleteSimilarItemAlertFromList',
    async ({ savedItemId }, { getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        await deleteSimilarItemAlert({ authToken, deployment, savedItemId });
    }
);

export const removeSimilarItemAlert = createAsyncThunk<void, { lotId: number; savedItemId: number }>(
    'la/ui/similarItemAlerts/removeSimilarItemAlert',
    async ({ savedItemId }, { getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        await deleteSimilarItemAlert({ authToken, deployment, savedItemId });
    },
    {
        // Guards a double-click only. Deliberately not gated on the lookup's flag: the two race on
        // the post-login path and a shared guard would drop the user's action.
        condition: ({ lotId }, { getState }) => !isSimilarItemAlertSaving(getState(), lotId),
    }
);
